import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link2, Search, X } from 'lucide-react';
import { API } from '@/App';
import { Input } from '@/components/ui/input';

/**
 * "Insert platform link" — pick a real destination in the platform and get a proper link.
 *
 * Used wherever you write text a member will read. The link is inserted as markdown at the caret,
 * and MarkdownWithLinks makes it navigate inside the app rather than reloading the page.
 */
export function PlatformLinkPicker({ onInsert, onClose }) {
  const [groups, setGroups] = useState([]);
  const [query, setQuery] = useState('');

  useEffect(() => {
    axios.get(`${API}/platform-links`)
      .then((res) => setGroups(res.data.groups || []))
      .catch(() => setGroups([]));
  }, []);

  const filtered = groups
    .map((g) => ({
      ...g,
      items: g.items.filter((i) => i.label.toLowerCase().includes(query.toLowerCase())),
    }))
    .filter((g) => g.items.length);

  return (
    <div className="fixed inset-0 z-[60] flex items-start justify-center bg-black/60 p-4 overflow-y-auto"
      onClick={onClose} data-testid="platform-link-picker">
      <div className="w-full max-w-lg rounded-2xl bg-[#12161c] border border-white/10 mt-16 text-gray-100"
        onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-2 p-4 border-b border-white/10">
          <Link2 className="w-4 h-4 text-amber-300" />
          <p className="text-sm text-gray-100 font-medium flex-1">Insert a platform link</p>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-200"
            data-testid="platform-link-close"><X className="w-4 h-4" /></button>
        </div>

        <div className="p-4 border-b border-white/10">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-2.5 text-gray-500" />
            <Input value={query} onChange={(e) => setQuery(e.target.value)}
              placeholder="Search chats, modules, sections, shelves…"
              className="pl-9 bg-black/30 border-white/10 text-gray-100 placeholder:text-gray-500"
              data-testid="platform-link-search" />
          </div>
        </div>

        <div className="max-h-[52vh] overflow-y-auto p-2">
          {filtered.map((group) => (
            <div key={group.label} className="mb-3">
              <p className="px-2 py-1 text-[10px] uppercase tracking-[0.16em] text-gray-500">
                {group.label}
              </p>
              {group.items.map((item) => (
                <button key={item.path + item.label} type="button"
                  onClick={() => { onInsert(item); onClose(); }}
                  data-testid={`platform-link-item-${item.path.replace(/[^a-z0-9]+/gi, '-')}`}
                  className="w-full text-left px-3 py-2 rounded-lg text-sm text-gray-200 hover:bg-white/5">
                  {item.label}
                  <span className="block text-[11px] text-gray-500">{item.path}</span>
                </button>
              ))}
            </div>
          ))}
          {!filtered.length && (
            <p className="p-4 text-sm text-gray-500" data-testid="platform-link-empty">
              Nothing matches that.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

/** Insert `[label](path)` into a textarea at the caret and return the new text. */
export function insertLinkAtCaret(el, text, item) {
  const start = el?.selectionStart ?? text.length;
  const end = el?.selectionEnd ?? text.length;
  const selected = text.slice(start, end);
  const markdown = `[${selected || item.label}](${item.path})`;
  return {
    value: text.slice(0, start) + markdown + text.slice(end),
    caret: start + markdown.length,
  };
}

export default PlatformLinkPicker;
